import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';

const TiltShift = {
  name: 'TiltShift',
  uniforms: {
    tDiffuse: { value: null },
    uRes: { value: new THREE.Vector2(1, 1) },
    uAmount: { value: 5 },
    uFocus: { value: 0.46 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 uRes;
    uniform float uAmount, uFocus;
    varying vec2 vUv;
    void main() {
      float d = abs(vUv.y - uFocus);
      float r = smoothstep(0.16, 0.6, d) * uAmount;
      vec4 acc = texture2D(tDiffuse, vUv);
      float w = 1.0;
      if (r > 0.05) {
        for (int i = 0; i < 14; i++) {
          float a = float(i) * 2.39996;
          float rad = sqrt((float(i) + 0.5) / 14.0);
          vec2 o = vec2(cos(a), sin(a)) * rad * r / uRes;
          acc += texture2D(tDiffuse, vUv + o);
          w += 1.0;
        }
      }
      gl_FragColor = acc / w;
    }
  `,
};

const Grade = {
  name: 'Grade',
  uniforms: {
    tDiffuse: { value: null },
    uAspect: { value: 1 },
    uTime: { value: 0 },
    uSat: { value: 1.14 },
    uContrast: { value: 1.05 },
    uVignette: { value: 0.32 },
    uTint: { value: new THREE.Color(1, 1, 1) },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uAspect, uTime, uSat, uContrast, uVignette;
    uniform vec3 uTint;
    varying vec2 vUv;
    float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233)) + uTime) * 43758.5453); }
    void main() {
      vec3 c = texture2D(tDiffuse, vUv).rgb;
      float l = dot(c, vec3(0.299, 0.587, 0.114));
      c = mix(vec3(l), c, uSat);
      c = (c - 0.5) * uContrast + 0.5;
      c *= uTint;
      vec2 q = (vUv - 0.5) * vec2(uAspect, 1.0);
      float v = smoothstep(0.95, 0.25, length(q) * 1.05);
      c *= mix(1.0 - uVignette, 1.0, v);
      c += (hash(gl_FragCoord.xy) - 0.5) * 0.014;
      gl_FragColor = vec4(c, 1.0);
    }
  `,
};

export function createPost(renderer, scene, camera) {
  const size = renderer.getDrawingBufferSize(new THREE.Vector2());
  const rt = new THREE.WebGLRenderTarget(size.x, size.y, { type: THREE.HalfFloatType, samples: 4 });
  const composer = new EffectComposer(renderer, rt);
  composer.setPixelRatio(renderer.getPixelRatio());

  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  const bloom = new UnrealBloomPass(new THREE.Vector2(size.x / 2, size.y / 2), 0.32, 0.55, 0.92);
  composer.addPass(bloom);

  const tilt = new ShaderPass(TiltShift);
  composer.addPass(tilt);

  composer.addPass(new OutputPass());

  const grade = new ShaderPass(Grade);
  composer.addPass(grade);

  function setSize(w, h, pr) {
    composer.setPixelRatio(pr);
    composer.setSize(w, h);
    const bw = Math.floor(w * pr), bh = Math.floor(h * pr);
    tilt.uniforms.uRes.value.set(bw, bh);
    // scale tilt blur with resolution so it looks the same on any screen
    tilt.uniforms.uAmount.value = 5 * (bh / 1080);
    grade.uniforms.uAspect.value = w / h;
  }

  return { composer, bloom, tilt, grade, renderPass, setSize };
}
